import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import { AppConfirm } from "../../../utils/PopupUtil";
import API from "../../api/API";

interface MutationProps {
  tenantID: string;
  tenantSchemaID: string;
}

const useDeleteTenantSchema = () => {
  const queryClient = useQueryClient();
  const tenantIDAtomValue = useAtomValue(tenantIDAtom);

  const confirmDeleteNewestTenantSchema = async () => {
    const result = await AppConfirm({
      title: "Confirm Newest Tenant Schema Deletion",
      text: "Are you sure you want to delete the newest tenant schema?",
      icon: "error",
    });

    return result.isConfirmed;
  };

  const confirmDeleteTenantSchema = async () => {
    const result = await AppConfirm({
      title: "Confirm Tenant Schema Deletion",
      text: "Are you sure you want to delete this tenant schema?",
    });

    return result.isConfirmed;
  };

  const deleteLastTenantSchema = async () => {
    const result = await AppConfirm({
      title: "Last Schema",
      text: "You cannot delete the last schema of this Tenant.",
      showCancelButton: false,
    });

    return result.isConfirmed;
  };

  const deleteTenantSchema = useMutation({
    mutationFn: async ({ tenantID, tenantSchemaID }: MutationProps) => {
      return API.deleteTenantSchema(tenantID, tenantSchemaID);
    },
    onSuccess: (data, { tenantID }) => {
      queryClient.invalidateQueries({
        queryKey: [tenantID || tenantIDAtomValue, "records", "TenantSchema"],
      });
      newToastEvent("success", data?.data.message);
    },
    onError: () => {
      const errorMessage =
        "You don't have permissions to delete a tenant schema.";
      newToastEvent("error", errorMessage);
    },
  });

  return {
    confirmDeleteNewestTenantSchema: confirmDeleteNewestTenantSchema,
    confirmDeleteTenantSchema: confirmDeleteTenantSchema,
    deleteLastTenantSchema,
    deleteTenantSchema: deleteTenantSchema.mutate,
    deleteTenantSchemaLoading: deleteTenantSchema.isPending,
    deleteTenantSchemaError: deleteTenantSchema.isError,
  };
};

export default useDeleteTenantSchema;
